import React from 'react';
import { useIntl } from 'react-intl';

import { Grid, Notification } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';

import { usePaths } from '../../../routes/routesHelper';
import { linkTo } from '../../../utils';
import {
  ApiIcon,
  CallbackUrlIcon,
  EmailValidateIcon,
  LinkStylesUnderline,
  SignUpIcon,
  ToggleBox
} from '../../ui';
import NavigationSettingsLink from '../NavigationSettingsLink';
import { ContentWrapper, NotificationWrapper } from './style';

interface Props {
  projectId: string;
}

const PasswordlessLoginSettings = ({ projectId }: Props): JSX.Element => {
  const {
    apiSettingsPath,
    callbackUrlsSettingsPath,
    changeMethodPath,
    emailSettingsPath,
    passwordlessTypePath
  } = usePaths();
  const intl = useIntl();

  return (
    <>
      <ToggleBox
        title={intl.messages['navigation.settings.passwordless.label']}
        openByDefault
        dataTestId="navigation-block-auth">
        <ContentWrapper>
          <NotificationWrapper>
            <Notification icon={AlertExclamation}>
              <LinkStylesUnderline data-test-id="change-project-type">
                {intl.formatMessage(
                  { id: 'navigation.settings.classic-login.alert' },
                  linkTo({ href: changeMethodPath(projectId) })
                )}
              </LinkStylesUnderline>
            </Notification>
          </NotificationWrapper>
          <Grid cols={2} gap={24}>
            <NavigationSettingsLink
              dataTestId="link-navigation-callback-urls"
              icon={<CallbackUrlIcon />}
              titleInfo={{
                label: intl.messages['navigation.settings.callback.title'],
                path: callbackUrlsSettingsPath(projectId)
              }}
              subtitle={intl.messages['navigation.settings.callback.subtitle']}
            />
            <NavigationSettingsLink
              dataTestId="link-navigation-passwordless-type"
              icon={<SignUpIcon />}
              titleInfo={{
                label: intl.messages['navigation.settings.settings.title'],
                path: passwordlessTypePath(projectId)
              }}
              subtitle={intl.messages['navigation.settings.settings.subtitle']}
            />
            <NavigationSettingsLink
              dataTestId="link-navigation-email-settings"
              icon={<EmailValidateIcon />}
              titleInfo={{
                label: intl.messages['navigation.settings.email.title'],
                path: emailSettingsPath(projectId)
              }}
              subtitle={intl.messages['navigation.settings.email.subtitle']}
            />
            <NavigationSettingsLink
              dataTestId="link-navigation-api-settings"
              icon={<ApiIcon />}
              titleInfo={{
                label: intl.messages['navigation.settings.authentication.api-settings.title'],
                path: apiSettingsPath(projectId)
              }}
              subtitle={intl.messages['navigation.settings.authentication.api-settings.subtitle']}
            />
          </Grid>
        </ContentWrapper>
      </ToggleBox>
    </>
  );
};

export default PasswordlessLoginSettings;
